/**
 * Modello di dominio del configuratore Motornet.
 *
 * Raccoglie i tipi "puliti" e indipendenti dal trasporto usati dalla UI e
 * dall'engine: identificatori del veicolo, allestimenti, veicolo normalizzato,
 * catalogo degli accessori e stato di avanzamento del configuratore. Sono la
 * forma verso cui `normalize.ts` traduce i payload grezzi di Motornet.
 */
import type {
	MotornetAccessoryBucket,
	OrChoice,
} from '../configurator';
import type { MotornetVehicleKind } from 'src/motornet/types';

/** Id di un accessorio (idAccessorio di Motornet). */
export type OptionId = number;

/**
 * Riferimento univoco a un veicolo configurabile: il codice Eurotax, il tipo
 * di veicolo e il mese/anno di listino a cui si riferiscono prezzi e accessori.
 */
export type VehicleRef = {
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
	year: number;
	month: number;
};

/** Chiave stabile di un {@link VehicleRef}, per confronti e uso come cache key. */
export const vehicleRefKey = (ref: VehicleRef): string =>
	`${ref.eurotaxCode}:${ref.vehicleKind}:${ref.year}-${ref.month}`;

/** True se i due riferimenti puntano allo stesso veicolo (entrambi null compresi). */
export const sameVehicle = (
	a: VehicleRef | null,
	b: VehicleRef | null
): boolean => (!a || !b ? a === b : vehicleRefKey(a) === vehicleRefKey(b));

/**
 * Costruisce il {@link VehicleRef} di un allestimento datandolo a una certa
 * data (per default "ora"): mese/anno determinano il listino applicabile.
 */
export const vehicleRefOf = (
	trim: Trim,
	at: Date = new Date()
): VehicleRef => ({
	eurotaxCode: trim.eurotaxCode,
	vehicleKind: trim.vehicleKind,
	year: at.getFullYear(),
	month: at.getMonth() + 1,
});

/** Tipo di carrozzeria, usato per filtri e badge nella selezione modello. */
export type BodyType = 'Berlina' | 'SUV' | 'Station Wagon' | 'Elettrica';

/** Marca (brand) selezionabile nel primo passo del configuratore. */
export type Make = {
	make: string;
	code: string;
	logoUrl: string;
};

/** Modello di una marca, con i dati di sintesi mostrati in griglia. */
export type Model = {
	make: string;
	model: string;
	modelCode: string;
	img: string;
	fuels: string[];
	minPrice: number;
	bodyType: BodyType;
};

/**
 * Allestimento (versione) di un modello. È configurabile solo quando porta un
 * {@link Trim.eurotaxCode}: senza di esso non esiste il listino accessori
 * Motornet da caricare.
 */
export type Trim = {
	id: string;
	make: string;
	model: string;
	name: string;
	listPrice: number;
	img: string;
	fuel: string;
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
};

/** True se l'allestimento è configurabile (ha un codice Eurotax). */
export const isConfigurable = (trim: Trim): boolean => !!trim.eurotaxCode;

/** Riga di scheda tecnica: coppia etichetta/valore, con chiave di traduzione. */
export type SpecRow = {
	id: string;
	label: string;
	value: string;
	labelKey?: string;
	/** idAccessorio, quando la riga corrisponde a un accessorio di catalogo (dotazione di serie). */
	optionId?: OptionId;
};

/** Scheda tecnica del veicolo, suddivisa nelle sezioni mostrate in UI. */
export type VehicleSpecs = {
	equipment: SpecRow[];
	performance: SpecRow[];
	engine: SpecRow[];
	dimensions: SpecRow[];
	consumption: SpecRow[];
	emissions: SpecRow[];
};

/**
 * Prezzi del veicolo base (senza accessori). `basePrice` è il prezzo di base
 * (netto IVA, id_903), `retailPrice` il listino al pubblico, `roadPrice` la
 * messa su strada.
 */
export type VehiclePrices = {
	currency: string;
	basePrice: number;
	retailPrice: number;
	roadPrice: number;
	displayPrice: string;
};

/** Veicolo base normalizzato: anagrafica, prezzi, foto e scheda tecnica. */
export type Vehicle = {
	ref: VehicleRef;
	make: string;
	model: string;
	version: string;
	modelYear: string;
	bodyType: string;
	doors: string;
	drivenWheels: string;
	fuelType: string;
	fuelLabel: string;
	prices: VehiclePrices;
	photos: string[];
	heroPhoto: string;
	specs: VehicleSpecs;
};

/**
 * Accessorio di catalogo pronto per la UI: versione normalizzata dell'accessorio
 * Motornet, con etichette, prezzi e i flag di categoria (colore, pacchetto,
 * di serie, membro di pacchetto).
 */
export type CatalogOption = {
	id: OptionId;
	dictionaryId: number;
	code: string;
	name: string;
	subtitle: string;
	tooltip: string | null;
	bucket: MotornetAccessoryBucket;
	isPack: boolean;
	isColour: boolean;
	isStandard: boolean;
	category: string;
	categoryKey: string;
	retailPrice: number;
	basePrice: number;
	displayPrice: string;
	isPackMember: boolean;
	/**
	 * Vernici "sempre insieme": se questa opzione è il master di una coppia che ha
	 * inglobato il gemello tecnico Motornet, l'`idAccessorio` del gemello collassato.
	 * Il gemello non compare come voce propria; selezionare il master lo trascina
	 * comunque via `formulaInclusione` a livello di engine. Assente per le vernici
	 * non appaiate e per tutti gli altri accessori.
	 */
	twinId?: OptionId;
	/** `idDizionario` del gemello collassato (per i riferimenti via formula). */
	twinDictionaryId?: number;
	/** `codice` del gemello collassato, per etichettarne la riga. */
	twinCode?: string;
	/** Nome del gemello collassato, se distinto da quello del master. */
	twinName?: string;
	/** codiceDisponibilita di Motornet: S = di serie, P = a pagamento, G = gratuito. */
	availability: string;
	order: number;
};

/** Gruppo di accessori appartenenti allo stesso macrogruppo/categoria. */
export type CategoryGroup = {
	key: string;
	name: string;
	options: CatalogOption[];
};

/**
 * Catalogo degli accessori di un veicolo, con più indici pre-calcolati per
 * accesso rapido dalla UI:
 * - `byId` / `byDictionaryId`: lookup per idAccessorio e per idDizionario;
 * - `byBucket`: liste già separate per tipo (colori, optional, pacchetti, di serie);
 * - `categories`: raggruppamento per macrogruppo;
 * - `packMembers`: per ogni pacchetto, gli id degli accessori che contiene.
 */
export type Catalog = {
	options: CatalogOption[];
	byId: Map<OptionId, CatalogOption>;
	byDictionaryId: Map<number, CatalogOption[]>;
	byBucket: {
		colors: CatalogOption[];
		options: CatalogOption[];
		packs: CatalogOption[];
		standard: CatalogOption[];
	};
	categories: CategoryGroup[];
	packMembers: Map<OptionId, OptionId[]>;
};

/** Passo del wizard di configurazione. */
export type Step =
	| 'brand-selector'
	| 'model-selector'
	| 'customizer'
	| 'summary';
/** Sotto-sezione della schermata di personalizzazione. */
export type CustomizerSubsection = 'engine' | 'exterior-interior';
/** Scheda attiva nel customizer: colori, optional o pacchetti. */
export type CustomizerTab = 'colors' | 'options' | 'packs';

/**
 * Stato di avanzamento del configuratore, persistito e ripristinabile (URL /
 * store). `selectedOptions` contiene solo le scelte esplicite dell'utente
 * (i "pick"): gli accessori derivati vengono ricostruiti dall'engine.
 */
export type ConfiguratorProgress = {
	step: Step;
	makeCode: string | null;
	makeLabel: string | null;
	modelCode: string | null;
	modelLabel: string | null;
	vehicle: VehicleRef | null;
	subsection: CustomizerSubsection;
	tab: CustomizerTab;
	selectedOptions: OptionId[];
	choices: OrChoice[];
};

/**
 * Dominio di un accessorio rispetto alla configurazione corrente:
 * - `forced`: presente/obbligato (selezionato o trascinato da un vincolo/inclusione);
 * - `forbidden`: incompatibile (escluso da qualche catena attiva);
 * - `available`: liberamente selezionabile.
 */
export type Domain = 'forced' | 'forbidden' | 'available';

/** Etichetta migliore disponibile per un accessorio di catalogo. */
export const optionLabel = (o: CatalogOption): string =>
	o.name || o.subtitle || o.code || String(o.id);
